import React, { useEffect, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { Mail, CalendarDays, X } from "lucide-react";
import { httpClient } from "../HttpClient";

const DISMISSED_KEY = "google_services_prompt_dismissed";

// Propose de connecter Gmail + Google Calendar en un seul consentement, une
// fois par session (tant que non connecté), juste après la connexion à
// l'app — indépendant de la méthode de connexion (Google ou email/mot de
// passe). Les boutons séparés dans Mails/Planning restent utilisables
// indépendamment si l'utilisateur ignore cet écran ou déconnecte l'un des
// deux plus tard.
export default function ConnectGoogleServicesModal() {
  const [visible, setVisible] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  // true si déjà connecté aux deux mais avec un ancien scope Gmail (avant
  // l'ajout de gmail.modify, pour l'archivage) — même fenêtre, juste un
  // texte différent puisqu'il ne s'agit pas d'une première connexion.
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    Promise.all([
      httpClient.get("/api/users/me/google-mail/status"),
      httpClient.get("/api/users/me/google-calendar/status"),
    ])
      .then(([mail, calendar]) => {
        if (cancelled) return;
        const bothConnected = mail.data.connected && calendar.data.connected;
        if (!bothConnected) {
          setVisible(true);
        } else if (!mail.data.hasModifyScope) {
          setNeedsUpgrade(true);
          setVisible(true);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const connect = useGoogleLogin({
    flow: "auth-code",
    scope:
      "https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/calendar.events",
    access_type: "offline",
    prompt: "consent",
    onSuccess: async (resp) => {
      setConnecting(true);
      setError(null);
      try {
        await httpClient.post("/api/users/me/google-services/connect", { code: resp.code });
        setVisible(false);
      } catch (err) {
        setError(err.response?.data?.message || "Erreur lors de la connexion aux services Google.");
      } finally {
        setConnecting(false);
      }
    },
    onError: () => {
      setError("Échec de la connexion Google.");
      setConnecting(false);
    },
  });

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-[380px] flex flex-col items-center gap-5 text-center relative">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          title="Plus tard"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="w-14 h-14 rounded-full bg-[#840040]/10 flex items-center justify-center">
          <Mail className="w-7 h-7 text-[#840040]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-1">
            {needsUpgrade ? "Mettre à jour l'autorisation Gmail" : "Connecter Gmail et Agenda"}
          </h2>
          <p className="text-sm text-slate-500">
            {needsUpgrade
              ? "Une nouvelle fonctionnalité (classer et archiver un mail directement dans Gmail) nécessite une autorisation supplémentaire — reconnectez-vous en un clic pour l'activer."
              : "Accédez à vos mails et à votre agenda Google directement depuis MyALFA Worksync, en un seul clic."}
          </p>
        </div>
        {!needsUpgrade && (
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Mails
            </span>
            <span className="flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5" /> Agenda
            </span>
          </div>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button
          onClick={() => connect()}
          disabled={connecting}
          className="flex items-center gap-2 h-10 px-6 rounded-xl text-sm font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors w-full justify-center disabled:opacity-50"
        >
          {connecting ? "Connexion…" : needsUpgrade ? "Mettre à jour" : "Connecter Gmail + Agenda"}
        </button>
        <button
          onClick={dismiss}
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
}
